import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DialogComponent, Modal } from "widgets";
import { useOverlay } from "widgets/hooks";

import { completeIntroduction, interruptIntroduction } from "@/redux/modules/auth.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";

import { type IIntroductionSlide, introductionSlides } from "../../app-utils/help.js";
import style from "./styles/Introduction.module.scss";

const Introduction = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { overlayProps, state } = useOverlay({
    defaultOpen: true,
    onOpenChange(isOpen) {
      if (!isOpen) {
        dispatch(interruptIntroduction());
      }
    },
  });

  const appName = useSelector<IAppState, string>((state: IAppState) =>
    state.config.configuration ? (state.config.configuration.application?.name ?? "") : "",
  );
  const introSlides = introductionSlides(appName);
  const [activeSlide, setActiveSlide] = useState(introSlides[0]);

  const nextSlideIndex = () => {
    const currentIndex: number = introSlides.findIndex(
      (slide: IIntroductionSlide) => slide.eventKey === activeSlide.eventKey,
    );
    return currentIndex === introSlides.length - 1 ? null : currentIndex + 1;
  };

  const previousSlideIndex = () => {
    const currentIndex: number = introSlides.findIndex(
      (slide: IIntroductionSlide) => slide.eventKey === activeSlide.eventKey,
    );
    return currentIndex === 0 ? null : currentIndex - 1;
  };

  const nextSlide = () => {
    const nextIndex = nextSlideIndex();
    if (nextIndex) {
      setActiveSlide(introSlides[nextIndex]);
    } else {
      dispatch(completeIntroduction());
    }
  };

  const previousSlide = () => {
    const previousIndex = previousSlideIndex();

    if (previousIndex !== null) {
      setActiveSlide(introSlides[previousIndex]);
    } else {
      dispatch(completeIntroduction());
    }
  };

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        title={activeSlide.title}
        closeFn={() => dispatch(interruptIntroduction())}
        mainButton={{
          label: "Next",
          onPress: nextSlide,
        }}
        secondaryButton={{
          label: "Back",
          onPress: previousSlide,
          isDisabled: previousSlideIndex() == null,
        }}
        width={900}
      >
        <div className={style.content}>
          <div className={style.column}>{activeSlide.text}</div>
          <div className={style.column}>
            <img src={activeSlide.image} alt={"Introduction Slide"} />
          </div>
        </div>
      </DialogComponent>
    </Modal>
  );
};

export { Introduction };
